import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../../components/breadcrumbs'
import Footer from '../../../components/footer'
import { getCanonicalUrl } from '../../../utils/canonical'
import DarkNav from '../../../components/dark-nav'
import './zapier-feature.css'

const SendSms = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Zapier', path: '/integrations/zapier' },
    { label: 'Send SMS' }
  ]

  const useCases = [
    {
      title: "Order Confirmation SMS for Australian E-commerce",
      industry: "E-commerce & Retail",
      description: "Send instant order confirmation SMS messages to Australian customers when new orders are placed.",
      trigger: "Shopify - New Order",
      steps: [
        "Customer completes purchase on Shopify store",
        "Zapier triggers on new order event",
        "Extract: Customer phone number, order number, total amount",
        "DataFlows SMS sends: 'Thanks for your order #12345! Total: $127.50. Track delivery: [link]'",
        "Customer receives confirmation instantly",
        "Reduces 'where is my order?' support calls"
      ]
    },
    {
      title: "Appointment Reminders for Australian Healthcare",
      industry: "Healthcare & Medical",
      description: "Automatically send SMS appointment reminders to patients 24 hours before their scheduled visit.",
      trigger: "Google Calendar - Event Start (24 hours before)",
      steps: [
        "Google Calendar event starts in 24 hours",
        "Extract: Patient name, appointment time, doctor name",
        "DataFlows SMS sends: 'Hi Sarah, reminder: Appointment with Dr. Smith tomorrow at 2:30 PM. Reply C to confirm or R to reschedule.'",
        "Patient replies with C or R",
        "Response logged to Google Sheets",
        "Staff receives notification if rescheduling needed"
      ]
    },
    {
      title: "Lead Notification SMS for Real Estate Agents",
      industry: "Real Estate",
      description: "Alert Australian real estate agents instantly when new property leads come through.",
      trigger: "Gravity Forms - New Form Submission",
      steps: [
        "Potential buyer submits property enquiry form",
        "Extract: Lead name, phone, property interested in, budget",
        "DataFlows SMS to agent: 'New lead: John M. interested in 123 Smith St. Budget: $850k. Call: 0404 235 133'",
        "Agent contacts lead within 5 minutes",
        "Higher conversion rate due to fast response"
      ]
    },
    {
      title: "Delivery Status Updates for Logistics",
      industry: "Logistics & Delivery",
      description: "Keep Australian customers informed with real-time delivery status SMS notifications.",
      trigger: "Airtable - Record Updated (Status Field)",
      steps: [
        "Delivery status updated in Airtable: 'Out for Delivery'",
        "Filter: Only trigger when status = 'Out for Delivery'",
        "Extract: Customer name, delivery address, driver name, ETA",
        "DataFlows SMS: 'Hi James, your package is out for delivery! Driver: Mike, ETA: 2:00-4:00 PM today. Track: [link]'",
        "Customer receives real-time update",
        "Reduces missed deliveries"
      ]
    },
    {
      title: "Event Registration Confirmation SMS",
      industry: "Events & Conferences",
      description: "Send instant SMS confirmations to attendees when they register for Australian events.",
      trigger: "Eventbrite - New Order",
      steps: [
        "Attendee registers for event on Eventbrite",
        "Extract: Attendee name, event name, date, ticket type",
        "DataFlows SMS: 'Thanks for registering, Emma! Sydney Marketing Summit - June 15, 2025. VIP Pass. Details: [link]'",
        "SMS includes calendar invite link",
        "Send reminder SMS 7 days before event",
        "Send 'event starting soon' SMS on event day"
      ]
    },
    {
      title: "Customer Support Ticket Created Notification",
      industry: "Customer Support",
      description: "Notify customers via SMS when their support ticket is created and include ticket number.",
      trigger: "Zendesk - New Ticket",
      steps: [
        "Customer creates support ticket via email or web",
        "Extract: Customer name, ticket number, priority",
        "DataFlows SMS: 'Hi David, support ticket #4521 created. We'll respond within 2 hours. Track: [link]'",
        "When ticket is resolved, send follow-up SMS",
        "Request feedback via SMS survey link"
      ]
    },
    {
      title: "Payment Reminder SMS for Overdue Invoices",
      industry: "Accounting & Finance",
      description: "Automatically send polite SMS payment reminders to Australian clients with overdue invoices.",
      trigger: "Xero - Invoice Overdue",
      steps: [
        "Invoice becomes 7 days overdue in Xero",
        "Extract: Client name, invoice number, amount due",
        "DataFlows SMS: 'Hi Sarah, friendly reminder: Invoice #INV-1234 ($450) is now 7 days overdue. Pay here: [link]'",
        "If still unpaid after 14 days, escalate to phone call",
        "Log all SMS communications in Xero"
      ]
    },
    {
      title: "Emergency Alert System for Schools",
      industry: "Education",
      description: "Send urgent SMS alerts to parents and staff during Australian school emergencies.",
      trigger: "Google Forms - Emergency Alert Submitted",
      steps: [
        "School admin submits emergency alert form",
        "Extract: Alert type, message, severity",
        "DataFlows SMS to all parent contacts: 'SCHOOL ALERT: Early dismissal today due to severe weather. Pick up by 1:00 PM.'",
        "Send to 500+ parents simultaneously",
        "Confirmation SMS sent when alert resolved",
        "Log all alerts in Google Sheets"
      ]
    },
    {
      title: "Flash Sale SMS Campaign for Australian Retail",
      industry: "Retail & E-commerce",
      description: "Send targeted SMS blast to customers when flash sales or limited-time offers go live.",
      trigger: "Mailchimp - New Campaign Sent",
      steps: [
        "Marketing team launches email flash sale campaign",
        "Extract: Sale details, discount code, end time",
        "Filter: Send SMS only to VIP customer segment",
        "DataFlows SMS: '⚡ FLASH SALE: 40% off all sneakers for the next 4 hours! Code: FLASH40. Shop: [link]'",
        "Track SMS click-through rate",
        "Much higher open rate than email (98% vs 20%)"
      ]
    },
    {
      title: "Shipping Delay Notification System",
      industry: "E-commerce",
      description: "Proactively notify Australian customers via SMS when shipments are delayed.",
      trigger: "Shippo - Tracking Status Updated",
      steps: [
        "Shipping carrier updates tracking: 'Delayed in transit'",
        "Extract: Customer name, order number, new ETA",
        "DataFlows SMS: 'Hi Lisa, your order #7894 is delayed by 1 day due to weather. New ETA: Thursday. Sorry for the inconvenience!'",
        "Prevents customer frustration from unexpected delays",
        "Offer discount code for future purchase"
      ]
    },
    {
      title: "Booking Confirmation for Australian Hospitality",
      industry: "Hospitality & Tourism",
      description: "Send instant SMS booking confirmations for hotels, restaurants, and tours in Australia.",
      trigger: "OpenTable / Resy - New Reservation",
      steps: [
        "Customer makes restaurant booking online",
        "Extract: Customer name, date, time, party size, restaurant name",
        "DataFlows SMS: 'Booking confirmed! The Harbour Restaurant - Saturday 7:00 PM for 4 people. See you then! Reply C to cancel.'",
        "Send reminder SMS 4 hours before reservation",
        "If customer replies C, cancel booking automatically"
      ]
    },
    {
      title: "Staff Roster Change Notification",
      industry: "Workforce Management",
      description: "Alert Australian staff members instantly when their work roster changes.",
      trigger: "Deputy / When I Work - Shift Updated",
      steps: [
        "Manager updates employee shift in roster system",
        "Extract: Employee name, old shift, new shift, date",
        "DataFlows SMS: 'Hi Tom, shift update: Thursday changed from 9AM-5PM to 12PM-8PM. Reply OK to confirm.'",
        "Employee confirms via SMS reply",
        "Reduces no-shows from schedule confusion"
      ]
    }
  ]

  return (
    <div className="zapier-feature-container">
      <DarkNav />
      <Helmet>
        <title>Send SMS via Zapier - DataFlows SMS Integration Australia</title>
        <meta
          name="description"
          content="Automate SMS sending with Zapier and DataFlows. Send order confirmations, appointment reminders, alerts, and notifications from 6000+ apps. Australian SMS automation made easy."
        />
        <meta
          property="og:title"
          content="Send SMS via Zapier - DataFlows SMS Integration Australia"
        />
        <meta
          property="og:description"
          content="Automate SMS sending with Zapier and DataFlows. Send order confirmations, appointment reminders, alerts, and notifications from 6000+ apps. Australian SMS automation made easy."
        />
        <link rel="canonical" href={getCanonicalUrl('/integrations/zapier/actions/send-sms')} />
      </Helmet>

      <div className="zapier-feature-hero" data-aos="fade-down">
        <div className="breadcrumbs-wrapper">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        <h1 className="zapier-feature-title">Send SMS</h1>
        <p className="zapier-feature-subtitle">
          Automatically send SMS messages from 6,000+ apps with Zapier and DataFlows. Perfect for Australian businesses needing reliable SMS automation.
        </p>
        <div className="zapier-feature-badge">
          <span className="badge-type">Action</span>
          <span className="badge-separator">•</span>
          <span className="badge-plan">All DataFlows Plans</span>
        </div>
      </div>

      <div className="zapier-feature-content">
        {/* Overview Section */}
        <section className="feature-section" data-aos="fade-up">
          <h2>What Does This Action Do?</h2>
          <p className="feature-description">
            The <strong>Send SMS</strong> action allows you to automatically send text messages to Australian and international phone numbers when triggered by events in your favourite apps. Whether it's order confirmations, appointment reminders, delivery updates, or emergency alerts - connect any of Zapier's 6,000+ apps to DataFlows and send SMS messages instantly.
          </p>
          <div className="feature-highlight">
            <p><strong>Popular Australian Use Cases:</strong> E-commerce order confirmations, medical appointment reminders, real estate lead alerts, event registration confirmations, payment reminders, emergency notifications, and customer support updates.</p>
          </div>
        </section>

        {/* How It Works */}
        <section className="feature-section" data-aos="fade-up">
          <h2>How It Works</h2>
          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Choose Your Trigger App</h3>
                <p>Select any of 6,000+ apps like Shopify, WooCommerce, Google Calendar, Salesforce, Airtable, Gravity Forms, or Typeform as your trigger.</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Configure DataFlows Send SMS Action</h3>
                <p>Map your trigger data to SMS fields: recipient phone number (Australian mobile format: 04XX XXX XXX), message content, and optional sender ID.</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>SMS Sent Automatically</h3>
                <p>When your trigger event occurs, DataFlows instantly sends the SMS to the recipient. Delivery typically takes 1-5 seconds in Australia.</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Track Delivery (Optional)</h3>
                <p>Use the SMS Delivery Status trigger to track when messages are delivered, failed, or bounced. Perfect for mission-critical notifications.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Configuration Fields */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Action Configuration Fields</h2>
          <div className="config-fields">
            <div className="config-field">
              <h4>Phone Number <span className="required-badge">Required</span></h4>
              <p>The recipient's mobile number. Australian format: <code>0404235133</code> or <code>+61404235133</code>. International numbers supported with country code.</p>
            </div>
            <div className="config-field">
              <h4>Message <span className="required-badge">Required</span></h4>
              <p>The SMS text content (max 160 characters for single SMS, longer messages split automatically). Supports dynamic data from previous Zapier steps.</p>
            </div>
            <div className="config-field">
              <h4>Sender ID <span className="optional-badge">Optional</span></h4>
              <p>Custom sender name (up to 11 characters) shown instead of phone number. Example: "ACME Store". Not supported by all Australian carriers.</p>
            </div>
            <div className="config-field">
              <h4>Reference <span className="optional-badge">Optional</span></h4>
              <p>Your internal reference ID for tracking (e.g., order number, ticket ID, customer ID). Helps correlate SMS with business records.</p>
            </div>
          </div>
        </section>

        {/* Real-World Use Cases */}
        <section className="feature-section use-cases-section" data-aos="fade-up">
          <h2>Real-World Australian Use Cases</h2>
          <p className="section-intro">
            Here are {useCases.length} proven ways Australian businesses use the Send SMS action to improve customer experience and operational efficiency:
          </p>

          {useCases.map((useCase, index) => (
            <div key={index} className="use-case-card" data-aos="fade-up" data-aos-delay={index * 50}>
              <div className="use-case-header">
                <h3>{useCase.title}</h3>
                <span className="industry-tag">{useCase.industry}</span>
              </div>
              <p className="use-case-description">{useCase.description}</p>
              <div className="use-case-trigger">
                <strong>Trigger:</strong> {useCase.trigger}
              </div>
              <div className="workflow-breakdown">
                <h4>Workflow Breakdown:</h4>
                <ol className="workflow-steps-list">
                  {useCase.steps.map((step, stepIndex) => (
                    <li key={stepIndex}>{step}</li>
                  ))}
                </ol>
              </div>
            </div>
          ))}
        </section>

        {/* Setup Guide */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Quick Setup Guide</h2>
          <div className="setup-steps">
            <h3>Step 1: Create Your Zap</h3>
            <ol>
              <li>Log in to your Zapier account</li>
              <li>Click "Create Zap"</li>
              <li>Search for and select your trigger app (e.g., "Shopify", "Google Calendar")</li>
              <li>Choose the specific trigger event (e.g., "New Order", "Event Start")</li>
              <li>Connect your account and configure trigger settings</li>
              <li>Test the trigger to ensure data is flowing correctly</li>
            </ol>

            <h3>Step 2: Add DataFlows Send SMS Action</h3>
            <ol>
              <li>Click the "+" button to add an action step</li>
              <li>Search for "DataFlows SMS" and select it</li>
              <li>Choose the "Send SMS" action event</li>
              <li>Connect your DataFlows account (you'll need your API key from the DataFlows dashboard)</li>
              <li>Click "Continue"</li>
            </ol>

            <h3>Step 3: Configure SMS Fields</h3>
            <ol>
              <li><strong>Phone Number:</strong> Map the phone field from your trigger (e.g., {'{'}{'{'} Customer Phone {'}'}{'}'} from Shopify)</li>
              <li><strong>Message:</strong> Compose your SMS using static text and dynamic fields:
                <div className="code-example">
                  Thanks for your order {'{'}{'{'} Order Number {'}'}{'}'} ! Total: ${'{'}{'{'} Total Price {'}'}{'}'} . Track: [link]
                </div>
              </li>
              <li><strong>Sender ID:</strong> (Optional) Enter your business name or leave blank</li>
              <li><strong>Reference:</strong> (Optional) Map order/ticket ID for tracking</li>
              <li>Click "Continue"</li>
            </ol>

            <h3>Step 4: Test & Activate</h3>
            <ol>
              <li>Click "Test action" to send a real SMS to verify everything works</li>
              <li>Check that the SMS was received correctly</li>
              <li>Make any necessary adjustments to message content or formatting</li>
              <li>Turn on your Zap!</li>
              <li>Monitor the Zap history to ensure SMS messages are sending successfully</li>
            </ol>
          </div>
        </section>

        {/* Best Practices */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Best Practices for Australian Businesses</h2>
          <div className="best-practices">
            <div className="practice-item">
              <h4>✅ Include Opt-Out Instructions</h4>
              <p>Add "Reply STOP to opt out" to marketing SMS messages. Required by Australian spam laws (Spam Act 2003).</p>
            </div>
            <div className="practice-item">
              <h4>✅ Keep Messages Concise</h4>
              <p>Under 160 characters = 1 SMS credit. Longer messages split into multiple parts and cost more. Get to the point quickly.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Personalize With Dynamic Data</h4>
              <p>Use customer names, order numbers, and specific details. "Hi Sarah, order #1234..." performs better than generic messages.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Include Clear Call-to-Action</h4>
              <p>Tell recipients what to do next: "Click here", "Reply YES", "Call us", or "Visit [link]". Make next steps obvious.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Use URL Shorteners for Links</h4>
              <p>Long URLs consume character count. Use bit.ly or your own branded shortener to save space.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Test with Australian Numbers First</h4>
              <p>Send test SMS to your own Australian mobile before activating the Zap. Verify formatting, tone, and links work correctly.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Set Up Delivery Tracking</h4>
              <p>Create a second Zap using the "SMS Delivery Status Changed" trigger to log failed/bounced messages for follow-up.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Respect Quiet Hours</h4>
              <p>Add filters to avoid sending SMS between 9 PM - 8 AM Australian time unless it's an emergency. Use Zapier's "Filter" step.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Monitor Credit Balance</h4>
              <p>Set up the "Low Balance Alert" trigger to notify you when SMS credits run low. Prevents disruption to critical workflows.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Validate Phone Numbers</h4>
              <p>Use Zapier's "Formatter" tool to clean phone numbers before sending (remove spaces, ensure Australian format). Reduces failed SMS.</p>
            </div>
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Common Issues & Solutions</h2>
          <div className="troubleshooting">
            <div className="issue-solution">
              <h4>❌ SMS Not Delivered</h4>
              <p><strong>Solutions:</strong></p>
              <ul>
                <li>Verify phone number is in correct format (04XX XXX XXX or +614XX XXX XXX)</li>
                <li>Check DataFlows account has sufficient credits</li>
                <li>Ensure recipient number is a valid Australian mobile (landlines don't receive SMS)</li>
                <li>Check the SMS Delivery Status trigger for error details</li>
              </ul>
            </div>
            <div className="issue-solution">
              <h4>❌ Message Contains {'{'}{'{'} Curly Braces {'}'}{'}'} </h4>
              <p><strong>Solution:</strong> You forgot to map the dynamic field. Click the field in Zapier and select the correct data from the dropdown - don't type it manually.</p>
            </div>
            <div className="issue-solution">
              <h4>❌ Zap Triggering Too Often</h4>
              <p><strong>Solution:</strong> Add a "Filter" step between trigger and action. Example: "Only continue if Order Status = Paid" to avoid sending SMS for draft orders.</p>
            </div>
            <div className="issue-solution">
              <h4>❌ International Numbers Failing</h4>
              <p><strong>Solution:</strong> Ensure phone number includes country code (e.g., +1 for USA, +44 for UK). Use Zapier Formatter to add country code if missing.</p>
            </div>
            <div className="issue-solution">
              <h4>❌ Sender ID Not Showing</h4>
              <p><strong>Note:</strong> Some Australian carriers (especially Telstra) don't support custom sender IDs and will show a numeric sender instead. This is carrier-dependent, not a DataFlows issue.</p>
            </div>
          </div>
        </section>

        {/* Advanced Tips */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Advanced Automation Tips</h2>
          <div className="advanced-tips">
            <h3>Multi-Step SMS Workflows</h3>
            <p>Combine Send SMS with other actions for sophisticated automations:</p>
            <ul>
              <li><strong>SMS + Email:</strong> Send SMS immediately for urgent matters, then follow up with detailed email</li>
              <li><strong>SMS + Database:</strong> Log all sent SMS messages to Google Sheets or Airtable for compliance/auditing</li>
              <li><strong>SMS + CRM:</strong> Update Salesforce/HubSpot contact record after sending SMS confirmation</li>
              <li><strong>SMS + Delays:</strong> Use Zapier's "Delay" step to send reminder SMS X hours/days after initial event</li>
            </ul>

            <h3>Conditional SMS Sending</h3>
            <p>Use Zapier's "Filter" and "Paths" features to send different SMS based on conditions:</p>
            <ul>
              <li>Send different messages to VIP vs regular customers</li>
              <li>Only send SMS for high-value orders (e.g., Total {'>'} $100)</li>
              <li>Different messages for different product categories</li>
              <li>Send to Australian numbers only (filter by country code)</li>
            </ul>

            <h3>Bulk SMS Campaigns</h3>
            <p>For sending to multiple recipients:</p>
            <ul>
              <li>Use Google Sheets as trigger with list of phone numbers</li>
              <li>Add "Looping" with Zapier's built-in loop feature</li>
              <li>Respect rate limits and quiet hours</li>
              <li>Track sent messages and responses in spreadsheet</li>
            </ul>
          </div>
        </section>

        {/* Related Features */}
        <section className="feature-section related-features" data-aos="fade-up">
          <h2>Related DataFlows Features</h2>
          <div className="related-cards">
            <Link to="/integrations/zapier/triggers/sms-delivery-status" className="related-card">
              <h4>SMS Delivery Status Changed</h4>
              <p>Track when your sent SMS messages are delivered, failed, or bounced. Perfect for mission-critical notifications.</p>
              <span className="feature-type">Trigger</span>
            </Link>
            <Link to="/integrations/zapier/triggers/new-sms-received" className="related-card">
              <h4>New SMS Received</h4>
              <p>Trigger automations when customers reply to your SMS messages. Enable two-way conversations.</p>
              <span className="feature-type">Trigger</span>
            </Link>
            <Link to="/integrations/zapier/triggers/low-balance-alert" className="related-card">
              <h4>Low Balance Alert</h4>
              <p>Get notified when your SMS credits run low. Prevent disruption to critical workflows.</p>
              <span className="feature-type">Trigger</span>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="feature-section cta-section" data-aos="fade-up">
          <div className="cta-content">
            <h2>Ready to Automate Your SMS Sending?</h2>
            <p>Join hundreds of Australian businesses using DataFlows + Zapier to send millions of automated SMS messages every month.</p>
            <div className="cta-buttons">
              <a
                href="https://zapier.com/apps/dataflows-sms/integrations"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Get Started with Zapier
              </a>
              <Link to="/integrations/zapier" className="btn-secondary">
                View All Features
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="scroll-to-top"
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  )
}

export default SendSms
